import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DoorOpen, LogIn, LogOut, Users, Clock } from 'lucide-react';
import VisitorRegister from '../hr/visitors/VisitorRegister';
import { apiGet, apiPut } from '../../lib/api';
import { formatISTDateTime } from '../../lib/format';
import { toast } from 'sonner';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const TABS = [
  { key: 'register', label: 'Register', icon: DoorOpen },
  { key: 'expected', label: 'Expected', icon: Clock },
  { key: 'inside', label: 'Inside', icon: Users },
];

export default function MobileVisitorCheckin() {
  const nav = useNavigate();
  const [tab, setTab] = useUrlTab('tab', 'register');
  const [expected, setExpected] = useState([]);
  const [inside, setInside] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([apiGet('/admin/visitors/today'), apiGet('/admin/visitors/active')])
      .then(([t, a]) => {
        setExpected((t?.visitors || []).filter((v) => v.status === 'expected'));
        setInside(a?.visitors || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (tab !== 'register') load(); }, [tab, load]);

  const checkIn = async (v) => {
    try { await apiPut(`/admin/visitors/${v.id}/check-in`, {}); toast.success('Checked in — host notified'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Check-in failed'); }
  };
  const checkOut = async (v) => {
    try { await apiPut(`/admin/visitors/${v.id}/check-out`, {}); toast.success('Checked out'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Check-out failed'); }
  };

  return (
    <div className="px-5 pt-5 pb-24 min-h-screen bg-slate-50" data-testid="mobile-visitor-checkin">
      <button onClick={() => nav('/m')} className="text-xs text-slate-500 flex items-center gap-1 mb-4"><ArrowLeft size={14} /> Home</button>
      <header className="mb-5 bg-gradient-to-br from-[#3b4872] to-[#0F172A] text-white rounded-2xl p-5 shadow-lg">
        <div className="flex items-center gap-2"><DoorOpen size={20} /><h1 className="font-display text-xl font-extrabold tracking-tight">Visitor Check-in</h1></div>
        <p className="text-xs text-white/80 mt-1">Register a walk-in, or check pre-registered visitors in / out.</p>
      </header>

      <div className="flex gap-1.5 mb-4">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`mvisit-tab-${key}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-display font-bold uppercase tracking-wider ${
              tab === key ? 'bg-[#3b4872] text-white shadow' : 'bg-white text-slate-500 border border-slate-200'
            }`}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {tab === 'register' && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
          <VisitorRegister mode="walkin" embedded onSaved={() => { toast.success('Visitor registered'); nav('/m'); }} />
        </div>
      )}

      {tab === 'expected' && (
        <div className="space-y-3" data-testid="mvisit-expected-list">
          {loading ? (
            <div className="text-center py-10 text-sm text-slate-400">Loading…</div>
          ) : expected.length === 0 ? (
            <div className="text-center py-10 text-slate-400"><Clock size={32} className="mx-auto mb-2 opacity-40" /><p className="text-sm">No visitors expected today.</p></div>
          ) : expected.map((v) => (
            <div key={v.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex items-center justify-between" data-testid={`mvisit-expected-${v.id}`}>
              <div>
                <div className="font-display font-bold text-sm">{v.visitor_name}</div>
                <div className="text-[11px] text-slate-500">{v.host_name || '—'} · {v.purpose || ''}</div>
              </div>
              <button onClick={() => checkIn(v)} data-testid={`mvisit-checkin-${v.id}`}
                className="flex items-center gap-1 text-[11px] font-display font-bold uppercase px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200">
                <LogIn size={13} /> Check In
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === 'inside' && (
        <div className="space-y-3" data-testid="mvisit-inside-list">
          {loading ? (
            <div className="text-center py-10 text-sm text-slate-400">Loading…</div>
          ) : inside.length === 0 ? (
            <div className="text-center py-10 text-slate-400"><Users size={32} className="mx-auto mb-2 opacity-40" /><p className="text-sm">Nobody on premises right now.</p></div>
          ) : inside.map((v) => (
            <div key={v.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex items-center justify-between" data-testid={`mvisit-inside-${v.id}`}>
              <div>
                <div className="font-display font-bold text-sm">{v.visitor_name}</div>
                <div className="text-[11px] text-slate-500">{v.host_name || '—'} · in {v.in_time ? formatISTDateTime(v.in_time) : '—'}</div>
              </div>
              <button onClick={() => checkOut(v)} data-testid={`mvisit-checkout-${v.id}`}
                className="flex items-center gap-1 text-[11px] font-display font-bold uppercase px-3 py-2 rounded-xl bg-blue-50 text-blue-700 border border-blue-200">
                <LogOut size={13} /> Check Out
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
